import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { placesApi } from '@/lib/api';
import { MapPin, ChevronLeft, Home } from 'lucide-react';
import Colors from '@/constants/colors';
import PageContainer from '@/components/PageContainer';
import ContentSection from '@/components/ContentSection';
import { DetailActionButtons } from '@/components/DetailActionButtons';
import { ScrollingBackButton } from '@/components/ScrollingBackButton';
import { useNavigationSource } from '@/hooks/useNavigationSource';

export default function PlaceDetail() {
  const { language } = useAppLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDirectLink } = useNavigationSource();

  const { data: place, isLoading, error } = useQuery({
    queryKey: ['places', id, language],
    queryFn: () => id ? placesApi.getById(id, language) : null,
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>Loading place...</p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error || !place) {
    return (
      <PageContainer>
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={styles.notFound}>Place not found</p>
        </ContentSection>
      </PageContainer>
    );
  }

  const name = language === 'ru' ? place.name_ru || place.name : place.name;
  const description = language === 'ru' ? place.description_ru || place.description : place.description || place.summary;

  return (
    <PageContainer>
      <ScrollingBackButton />

      {/* Hero Section */}
      <div style={styles.heroContainer}>
        <img
          src={place.image_url || place.imageUrl}
          alt={name}
          style={styles.heroImage}
          loading="lazy"
          decoding="async"
        />
        <div style={styles.heroOverlay} />
        <div style={styles.heroContent}>
          <div style={styles.heroTopRow}>
            <button
              style={styles.backButton}
              onClick={() => isDirectLink ? navigate('/') : navigate(-1)}
              aria-label={isDirectLink ? 'Go to home' : 'Go back'}
            >
              {isDirectLink ? <Home size={24} color="white" /> : <ChevronLeft size={24} color="white" />}
            </button>
            <DetailActionButtons
              id={place.id}
              title={name}
              shareUrl={`/places/${place.id}`}
              hideShare={isDirectLink}
            />
          </div>
          <div>
            <h1 style={styles.heroTitle}>{name}</h1>
            <div style={styles.heroLocationContainer}>
              <MapPin size={16} color="rgba(255,255,255,0.9)" />
              <p style={styles.heroLocation}>{place.location}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Badge */}
      <ContentSection style={styles.badgeContainer}>
        <span style={styles.badge}>{place.category?.toUpperCase()}</span>
      </ContentSection>

      {/* Description */}
      <ContentSection style={{ padding: '0 24px' }}>
        <p style={styles.bodyText}>{description}</p>
      </ContentSection>

      {/* Spacing for bottom nav */}
      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  heroContainer: {
    position: 'relative',
    width: '100%',
    height: '300px',
    overflow: 'hidden',
  } as React.CSSProperties,

  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  } as React.CSSProperties,

  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6))',
  } as React.CSSProperties,

  heroContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '16px 24px',
  } as React.CSSProperties,

  heroTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,

  backButton: {
    background: 'rgba(0,0,0,0.4)',
    border: 'none',
    borderRadius: '8px',
    padding: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s ease',
  } as React.CSSProperties,

  heroTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: '700',
    color: 'white',
    lineHeight: '1.2',
    marginBottom: '8px',
  } as React.CSSProperties,

  heroLocationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  } as React.CSSProperties,

  heroLocation: {
    margin: 0,
    fontSize: '14px',
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '600',
  } as React.CSSProperties,

  badgeContainer: {
    padding: '24px 24px 0',
  } as React.CSSProperties,

  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    backgroundColor: Colors.secondary,
    color: Colors.surface,
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'uppercase' as const,
  } as React.CSSProperties,

  bodyText: {
    fontSize: '16px',
    color: Colors.text,
    lineHeight: '24px',
    margin: 0,
    marginBottom: '16px',
  } as React.CSSProperties,

  notFound: {
    color: Colors.textSecondary,
  } as React.CSSProperties,
};
